import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService, PhotographyApiService } from './api.service';
import { PhotographyListResponse, PhotographyVendor } from '../models/photography-vendor.model';
import {
  PhotographyAddZipCodesResult, PhotographyBundle, PhotographyBundleInclude, PhotographyBundlePrice,
  PhotographyBundleWithRegions, PhotographyPhotographer, PhotographyProduct, PhotographyVendorRegion,
  PhotographyVendorRegionZipCode,
} from '../models/photography-catalog.model';

/**
 * Mirrors CreateVendorRequest in @lcms-photography/core. Updates send any subset of these,
 * and null clears an optional field (null is update only).
 */
export interface PhotographyVendorRequest {
  name?: string;
  email?: string | null;
  /** Free text, stored as given */
  phone?: string | null;
  /** Full URL of the vendor's logo */
  logoImage?: string | null;
  useCustomScheduling?: boolean;
  customSchedulingUrl?: string | null;
  mongoId?: string;
}

/** Vendors in the lcms-photography catalog */
@Injectable({
  providedIn: 'root',
})
export class PhotographyVendorService {
  private api = inject(PhotographyApiService);
  private managementApi = inject(ApiService);

  readonly resource = 'vendors';

  /** The API caps a page at 200, which covers every vendor we have today */
  list$(limit = 200, offset = 0): Observable<PhotographyVendor[]> {
    return this.api.get<PhotographyListResponse<PhotographyVendor>>(this.resource, { limit, offset }).pipe(
      map((response) => response.items),
    );
  }

  get$(vendorId: string): Observable<PhotographyVendor> {
    return this.api.get<PhotographyVendor>(`${this.resource}/${vendorId}`);
  }

  create(vendor: PhotographyVendorRequest): Promise<PhotographyVendor> {
    return this.api.create<PhotographyVendor, PhotographyVendorRequest>(this.resource, vendor);
  }

  update(vendorId: string, vendor: PhotographyVendorRequest): Promise<PhotographyVendor> {
    return this.api.update<PhotographyVendor, PhotographyVendorRequest>(`${this.resource}/${vendorId}`, vendor);
  }

  regions$(vendorId: string): Observable<PhotographyVendorRegion[]> {
    return this.api.get<{ items: PhotographyVendorRegion[] }>(`${this.resource}/${vendorId}/regions`).pipe(
      map((response) => response.items),
    );
  }

  getRegion$(vendorId: string, vendorRegionId: string): Observable<PhotographyVendorRegion> {
    return this.api.get<PhotographyVendorRegion>(this.regionPath(vendorId, vendorRegionId));
  }

  createRegion(vendorId: string, name: string): Promise<PhotographyVendorRegion> {
    const route = `${this.resource}/${vendorId}/regions`;
    return this.api.create<PhotographyVendorRegion, { name: string }>(route, { name });
  }

  updateRegion(vendorId: string, vendorRegionId: string, name: string): Promise<PhotographyVendorRegion> {
    const route = this.regionPath(vendorId, vendorRegionId);
    return this.api.update<PhotographyVendorRegion, { name: string }>(route, { name });
  }

  /** Rejects with a 409 while prices still reference the region */
  deleteRegion(vendorId: string, vendorRegionId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(this.regionPath(vendorId, vendorRegionId)));
  }

  regionZipCodes$(vendorId: string, vendorRegionId: string): Observable<PhotographyVendorRegionZipCode[]> {
    const route = `${this.regionPath(vendorId, vendorRegionId)}/zip-codes`;
    return this.api.get<{ items: PhotographyVendorRegionZipCode[] }>(route).pipe(map((response) => response.items));
  }

  /** Adds 5-digit zips or prefixes like "802*". Entries that match nothing come back in `unresolved`. */
  addRegionZipCodes(
    vendorId: string,
    vendorRegionId: string,
    zipCodes: string[],
  ): Promise<PhotographyAddZipCodesResult> {
    const route = `${this.regionPath(vendorId, vendorRegionId)}/zip-codes`;
    return firstValueFrom(this.api.put<PhotographyAddZipCodesResult>(route, { zipCodes }));
  }

  removeRegionZipCode(vendorId: string, vendorRegionId: string, zipCodeId: string): Promise<void> {
    const route = `${this.regionPath(vendorId, vendorRegionId)}/zip-codes/${zipCodeId}`;
    return firstValueFrom(this.api.delete<void>(route));
  }

  /** Linking a photographer who is already linked is a no-op */
  linkPhotographer(vendorId: string, photographerId: string, names?: { firstName?: string; lastName?: string }): Promise<void> {
    return firstValueFrom(this.managementApi.put<void>(`photography/${this.resource}/${vendorId}/photographers/${photographerId}`, names ?? {}));
  }

  unlinkPhotographer(vendorId: string, photographerId: string): Promise<void> {
    return firstValueFrom(this.managementApi.delete<void>(`photography/${this.resource}/${vendorId}/photographers/${photographerId}`));
  }

  products$(vendorId: string): Observable<PhotographyProduct[]> {
    return this.listByVendor$<PhotographyProduct>('products', vendorId);
  }

  bundles$(vendorId: string): Observable<PhotographyBundle[]> {
    return this.listByVendor$<PhotographyBundle>('bundles', vendorId);
  }

  /**
   * A vendor can have several bundles with the same name (they differ by included products), so pair
   * each bundle with the regions it is priced in and the products it includes to tell them apart. This is
   * a bundle-prices and an includes call per bundle until lcms-photography can return them with the bundle list.
   */
  bundlesWithRegions$(vendorId: string): Observable<PhotographyBundleWithRegions[]> {
    return this.bundles$(vendorId).pipe(
      switchMap((bundles) => (bundles.length ? forkJoin(bundles.map((bundle) => forkJoin({
        vendorRegionIds: this.bundleRegionIds$(bundle.bundleId),
        includeProductIds: this.bundleIncludeIds$(bundle.bundleId),
      }).pipe(
        map(({ vendorRegionIds, includeProductIds }) => ({ bundle, vendorRegionIds, includeProductIds })),
      ))) : of([]))),
    );
  }

  photographers$(vendorId: string): Observable<PhotographyPhotographer[]> {
    return this.listByVendor$<PhotographyPhotographer>('photographers', vendorId);
  }

  /** The distinct vendor regions a bundle has prices in. A failed lookup reads as no regions rather than failing the page. */
  private bundleRegionIds$(bundleId: string): Observable<string[]> {
    return this.api.get<PhotographyListResponse<PhotographyBundlePrice>>('bundle-prices', { bundleId, limit: 200 }).pipe(
      map((response) => [...new Set(response.items.map((price) => price.vendorRegionId))]),
      catchError(() => of([])),
    );
  }

  /** The products a bundle includes. A failed lookup reads as none rather than failing the page. */
  private bundleIncludeIds$(bundleId: string): Observable<string[]> {
    return this.api.get<{ items: PhotographyBundleInclude[] }>(`bundles/${bundleId}/includes`).pipe(
      map((response) => response.items.map((include) => include.productId)),
      catchError(() => of([])),
    );
  }

  private regionPath(vendorId: string, vendorRegionId: string) {
    return `${this.resource}/${vendorId}/regions/${vendorRegionId}`;
  }

  private listByVendor$<T>(resource: string, vendorId: string): Observable<T[]> {
    return this.api.get<PhotographyListResponse<T>>(resource, { vendorId, limit: 200 }).pipe(
      map((response) => response.items),
    );
  }
}
